import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FiArrowRight, FiCheckCircle, FiShield, FiTrendingUp, FiGlobe, FiCode, FiSmartphone, FiBarChart2, FiSearch, FiPenTool } from "react-icons/fi";
import SectionWrapper from "../components/SectionWrapper";
import hero1 from "../assets/hero1.jpg";
import hero2 from "../assets/hero2.jpg";
import hero3 from "../assets/hero3.jpg";
import hero4 from "../assets/hero4.jpg";
import { Helmet } from "react-helmet-async";

const heroSlides = [
  {
    image: hero1,
    title: "Building Intelligent Software & High-Impact Digital Experiences",
    subtitle: "GoPhoenix Data Solutions FZ LLC is a future-ready technology and digital marketing company delivering scalable software, data-driven platforms, and performance-oriented digital growth solutions.",
  },
  {
    image: hero2,
    title: "Custom Software Development & Engineering",
    subtitle: "We design and build secure, scalable, and high-performance software tailored to your business needs across all industries.",
  },
  {
    image: hero3,
    title: "Data-Driven Digital Marketing & SEO",
    subtitle: "We don't guess. We analyze, optimize, and scale your digital presence with ROI-focused marketing strategies.",
  },
  {
    image: hero4,
    title: "AI, Automation & Intelligent Systems",
    subtitle: "Leverage data intelligence, dashboards, and automation to reduce costs and increase efficiency across your operations.",
  },
];

const whyItems = [
  { icon: FiGlobe, title: "End-to-End Technology Partner", desc: "Complete digital solutions from concept to deployment and beyond." },
  { icon: FiBarChart2, title: "Data-Driven Digital Strategies", desc: "Every decision backed by analytics, insights, and real-time data." },
  { icon: FiShield, title: "Scalable & Secure Architecture", desc: "Enterprise-grade security with solutions built to grow with you." },
  { icon: FiTrendingUp, title: "ROI-Focused Marketing Execution", desc: "Performance campaigns engineered for leads, conversions, and growth." },
  { icon: FiCheckCircle, title: "UAE-Based, Globally Delivered", desc: "Headquartered in UAE with capabilities to serve clients worldwide." },
];

const services = [
  { icon: FiCode, title: "Custom Software Development", desc: "ERP, CRM, SaaS platforms, and enterprise dashboards.", path: "/software-solutions" },
  { icon: FiSmartphone, title: "Web & Mobile App Development", desc: "Responsive websites, PWAs, and native mobile applications.", path: "/services/web-app-development" },
  { icon: FiBarChart2, title: "AI, Data Analytics & Automation", desc: "BI dashboards, AI recommendations, and process automation.", path: "/services/ai-data-automation" },
  { icon: FiSearch, title: "SEO, PPC & Performance Marketing", desc: "Technical SEO, Google Ads, and conversion optimization.", path: "/services/seo-performance-marketing" },
  { icon: FiPenTool, title: "Branding, UI/UX & Creative Design", desc: "Brand identity, motion graphics, and creative assets.", path: "/services/branding-creative" },
];

const processSteps = ["Discover", "Design", "Develop", "Deploy", "Scale"];

const trustSignals = [
  "Agile & DevOps-Driven Delivery",
  "Enterprise-Grade Security",
  "SEO-Optimized & Conversion-Focused",
  "Transparent Communication",
];

const Home = () => {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <>
     
      {/* Hero Carousel */}
    
      <section className="relative h-[80vh] md:h-screen overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={current}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 }}
            className="absolute inset-0"
          >
            <img src={heroSlides[current].image} alt={heroSlides[current].title} className="w-full h-full object-cover" />
            <div className="hero-overlay" />
          </motion.div>
        </AnimatePresence>

        <div className="relative z-10 h-full flex items-center">
          <div className="container-max px-4 sm:px-6 lg:px-8 w-full">
            <AnimatePresence mode="wait">
              <motion.div
                key={current}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.8 }}
                className="max-w-3xl"
              >
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-montserrat font-bold text-primary-foreground mb-6 leading-tight">
                  {heroSlides[current].title}
                </h1>
                <p className="text-base md:text-xl text-primary-foreground/80 font-roboto mb-8 leading-relaxed">
                  {heroSlides[current].subtitle}
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/contact" className="btn-hero">Get a Free Consultation</Link>
                  <Link to="/services" className="btn-hero-outline">View Our Services</Link>
                </div>
              </motion.div>
            </AnimatePresence>

           
          </div>
        </div>
      </section>

      {/* Why GoPhoenix */}
      <SectionWrapper className="bg-muted">
        <div className="text-center mb-12">
          <h2 className="section-title mb-4">Why <span className="text-primary">GoPhoenix</span></h2>
          <p className="section-subtitle mx-auto">Your trusted technology and digital growth partner in the UAE and beyond.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
          {whyItems.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="card-hover text-center"
            >
              <item.icon className="w-10 h-10 text-primary mx-auto mb-4" />
              <h3 className="font-montserrat font-bold text-sm mb-2">{item.title}</h3>
              <p className="text-muted-foreground text-xs font-roboto">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </SectionWrapper>

      {/* Core Services */}
      <SectionWrapper>
        <div className="text-center mb-12">
          <h2 className="section-title mb-4">Core <span className="text-primary">Services</span></h2>
          <p className="section-subtitle mx-auto">End-to-end digital services that align technology, data, and marketing for business growth.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((svc, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
            >
              <Link to={svc.path} className="card-hover block group">
                <svc.icon className="w-12 h-12 text-primary mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="font-montserrat font-bold text-lg mb-2">{svc.title}</h3>
                <p className="text-muted-foreground font-roboto text-sm mb-4">{svc.desc}</p>
                <span className="text-primary font-semibold text-sm flex items-center gap-2 group-hover:gap-3 transition-all">
                  Learn More <FiArrowRight />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </SectionWrapper>

      {/* Animated Process */}
      <SectionWrapper className="bg-secondary text-secondary-foreground">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-montserrat font-bold text-primary-foreground mb-4">
            Our <span className="text-primary">Process</span>
          </h2>
          <p className="text-primary-foreground/70 font-roboto text-lg max-w-2xl mx-auto">
            A proven methodology that transforms ideas into scalable digital solutions.
          </p>
        </div>
        <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-0">
          {processSteps.map((step, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15 }}
              className="flex items-center"
            >
              <div className="bg-primary text-primary-foreground w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center font-montserrat font-bold text-sm md:text-base shadow-lg shadow-primary/30">
                {step}
              </div>
              {i < processSteps.length - 1 && (
                <div className="hidden md:block w-12 lg:w-20 h-0.5 bg-primary/40" />
              )}
              {i < processSteps.length - 1 && (
                <div className="block md:hidden w-0.5 h-8 bg-primary/40 mx-auto" />
              )}
            </motion.div>
          ))}
        </div>
      </SectionWrapper>

      {/* Trust Signals */}
      <SectionWrapper className="bg-muted">
        <div className="text-center mb-12">
          <h2 className="section-title mb-4">Why <span className="text-primary">Trust Us</span></h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {trustSignals.map((signal, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="flex items-center gap-4 bg-card p-6 rounded-xl shadow-md"
            >
              <FiCheckCircle className="w-8 h-8 text-primary flex-shrink-0" />
              <span className="font-montserrat font-semibold text-sm">{signal}</span>
            </motion.div>
          ))}
        </div>
      </SectionWrapper>

      {/* CTA */}
      <SectionWrapper className="bg-primary text-primary-foreground text-center">
        <h2 className="text-3xl md:text-4xl font-montserrat font-bold mb-4">Ready to Build Your Digital Future?</h2>
        <p className="text-primary-foreground/80 font-roboto text-lg mb-8 max-w-2xl mx-auto">
          Whether you need powerful software or scalable digital marketing, GoPhoenix Data Solutions is ready.
        </p>
        <Link to="/contact" className="btn-hero-outline">
          Request a Free Strategy Call
        </Link>
      </SectionWrapper>
    </>
  );
};

export default Home;



